import { ReactNode, useMemo } from "react";
import { Link, useMatch } from "react-router-dom";

import { Fight, ReportDoc } from "../api";
import PlayerCell from "../components/PlayerCell";
import ProgressBar from "../components/ProgressBar";
import SpellIcon from "../components/SpellIcon";
import { useReport } from "../context/ReportContext";
import { classFor } from "../data/classes";

const fmt = (n: number) => n.toLocaleString("en-US");
const fmtCompact = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(2)}M`
    : n >= 1_000
      ? `${(n / 1_000).toFixed(1)}k`
      : `${n}`;

/**
 * Overview dashboard - six tightly-packed cards giving raid leaders the
 * most actionable data at a glance: who tops DPS, who's eating avoidable
 * damage, are the debuffs up, did we kill the bosses. Designed to fit a
 * 1920x1080 viewport without scrolling.
 *
 * The view ignores `block` (which is just a backend placeholder) and reads
 * the full report from context so each card can pull from a different
 * metric without prop-drilling.
 */
export default function Overview() {
  const { report } = useReport();
  if (!report) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      <TopDamageCard report={report} />
      <ConsumablesCard report={report} />
      <AvoidableCard report={report} />
      <DebuffsCard report={report} />
      <EngineeringCard report={report} />
      <RaidStatsCard report={report} />
    </div>
  );
}

/* ------------------------------ Card shell ------------------------------ */

interface CardProps {
  title: string;
  href?: string;
  children: ReactNode;
  /** Hue for the title accent - tints the heading subtly. */
  tone?: "default" | "danger";
}

function Card({ title, href, children, tone = "default" }: CardProps) {
  const titleClass = tone === "danger" ? "text-red-300" : "text-gray-100";
  return (
    <section className="rounded-md border border-border bg-bg/40 flex flex-col min-h-0">
      <header className="flex items-center justify-between gap-2 px-3 pt-2.5 pb-2 border-b border-border/60">
        <h3
          className={`text-[11px] uppercase tracking-wider font-bold ${titleClass}`}
        >
          {title}
        </h3>
        {href && (
          <Link
            to={href}
            className="text-[12px] text-gray-500 hover:text-accent transition-colors"
          >
            View →
          </Link>
        )}
      </header>
      <div className="flex-1 min-h-0 p-3 text-sm">{children}</div>
    </section>
  );
}

/* ------------------------------ Damage cards ---------------------------- */

interface DamageRow {
  name?: string;
  type?: string;
  icon?: string;
  total: number;
  perSec?: number;
}

function TopDamageCard({ report }: { report: ReportDoc }) {
  const slug = useMatch("/r/:slug/:metric")?.params.slug ?? "";
  const rows = useMemo(
    () =>
      (
        (report.metrics["total_damage_done"]?.rows as unknown as DamageRow[]) ??
        []
      ).slice(0, 5),
    [report],
  );
  const top = rows[0]?.total ?? 0;
  return (
    <Card title="Damage Done" href={`/r/${slug}/total_damage_done`}>
      <DamageList rows={rows} top={top} highlight="amber" />
    </Card>
  );
}

function DamageList({
  rows,
  top,
  highlight,
}: {
  rows: DamageRow[];
  top: number;
  highlight: "amber" | "neutral" | "red";
}) {
  if (!rows.length) {
    return <p className="text-xs text-gray-500">No data.</p>;
  }
  return (
    <ol className="space-y-2">
      {rows.map((r, i) => {
        const cls = classFor(r.type);
        const pct = top ? (r.total / top) * 100 : 0;
        const totalColor =
          highlight === "red" ? "text-red-300" : "text-gray-100";
        return (
          <li key={`${r.name}-${i}`} className="flex items-center gap-2">
            <span className="w-4 text-xs tabular-nums text-gray-500">
              {i + 1}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <PlayerCell name={r.name} type={r.type} icon={r.icon} />
                <div className="flex items-baseline gap-2 flex-shrink-0">
                  <span
                    className={`text-sm tabular-nums font-semibold ${totalColor}`}
                  >
                    {fmtCompact(r.total)}
                  </span>
                  {r.perSec !== undefined && (
                    <span className="text-[10px] tabular-nums text-gray-500">
                      {fmtCompact(Math.round(r.perSec))}/s
                    </span>
                  )}
                </div>
              </div>
              <ProgressBar pct={pct} color={cls.color} height={4} />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/* ----------------------------- Consumables ------------------------------ */

interface ConsumablesRow {
  name?: string;
  type?: string;
  icon?: string;
  spec?: string;
  categories?: {
    flask_elixir?: {
      battleSlotPct?: number;
      guardianSlotPct?: number;
    };
    scrolls?: {
      byName?: Record<string, number>;
    };
  };
}

const FULL_COVERAGE_PCT = 95;

interface OffenderInfo {
  name?: string;
  type?: string;
  icon?: string;
  battlePct: number;
  guardianPct: number;
  agiPct: number;
  strPct: number;
  isPhysical: boolean;
  deficit: number;
}

function ConsumablesCard({ report }: { report: ReportDoc }) {
  const slug = useMatch("/r/:slug/:metric")?.params.slug ?? "";
  const block = report.metrics["consumables"];

  const view = useMemo(() => {
    const rows = (block?.rows ?? []) as ConsumablesRow[];
    const totalTimeMs = (block?.totalTimeMs as number | undefined) ?? 0;
    if (!rows.length || !totalTimeMs) return null;

    let battleSum = 0;
    let guardSum = 0;
    let battleCount = 0;
    let scrollPhysicalOk = 0;
    let scrollPhysicalTotal = 0;
    const offenders: OffenderInfo[] = [];

    for (const r of rows) {
      const fe = r.categories?.flask_elixir;
      const bPct = fe?.battleSlotPct ?? 0;
      const gPct = fe?.guardianSlotPct ?? 0;
      battleSum += bPct;
      guardSum += gPct;
      battleCount++;

      const physical = isPhysicalScrollSpec(r.type, r.spec);
      let agiPct = 0;
      let strPct = 0;
      if (physical) {
        const by = r.categories?.scrolls?.byName ?? {};
        const agiMs = Math.max(
          by["Scroll of Agility V"] ?? 0,
          by["Scroll of Agility IV"] ?? 0,
        );
        const strMs = Math.max(
          by["Scroll of Strength V"] ?? 0,
          by["Scroll of Strength IV"] ?? 0,
        );
        agiPct = (agiMs / totalTimeMs) * 100;
        strPct = (strMs / totalTimeMs) * 100;
        scrollPhysicalTotal++;
        if (
          agiPct >= FULL_COVERAGE_PCT &&
          strPct >= FULL_COVERAGE_PCT
        ) {
          scrollPhysicalOk++;
        }
      }

      let deficit = 0;
      if (bPct < FULL_COVERAGE_PCT) deficit += FULL_COVERAGE_PCT - bPct;
      if (gPct < FULL_COVERAGE_PCT) deficit += FULL_COVERAGE_PCT - gPct;
      if (physical && agiPct < FULL_COVERAGE_PCT)
        deficit += FULL_COVERAGE_PCT - agiPct;
      if (physical && strPct < FULL_COVERAGE_PCT)
        deficit += FULL_COVERAGE_PCT - strPct;

      if (deficit > 0) {
        offenders.push({
          name: r.name,
          type: r.type,
          icon: r.icon,
          battlePct: bPct,
          guardianPct: gPct,
          agiPct,
          strPct,
          isPhysical: physical,
          deficit,
        });
      }
    }

    offenders.sort((a, b) => b.deficit - a.deficit);

    return {
      battleAvg: battleCount ? battleSum / battleCount : 0,
      guardianAvg: battleCount ? guardSum / battleCount : 0,
      scrollOk: scrollPhysicalOk,
      scrollTotal: scrollPhysicalTotal,
      offenders: offenders.slice(0, 6),
    };
  }, [block]);

  if (!view) {
    return (
      <Card title="Consumables">
        <p className="text-xs text-gray-500">No consumable data.</p>
      </Card>
    );
  }

  return (
    <Card title="Consumables" href={`/r/${slug}/consumables`}>
      <div className="space-y-3 text-xs">
        <div className="space-y-1.5">
          <CoverageRow label="Battle elixir" pct={view.battleAvg} />
          <CoverageRow label="Guardian elixir" pct={view.guardianAvg} />
          {view.scrollTotal > 0 && (
            <CoverageRow
              label="Scrolls"
              pct={(view.scrollOk / view.scrollTotal) * 100}
            />
          )}
        </div>

        {view.offenders.length > 0 && (
          <div className="!mt-3 pt-2 border-t border-border/60">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">
              <span className="flex-1">Missing buffs</span>
              <span className="w-7 text-center" title="Battle elixir">
                E1
              </span>
              <span className="w-7 text-center" title="Guardian elixir">
                E2
              </span>
              <span className="w-7 text-center" title="Scroll of Agility">
                Agi
              </span>
              <span className="w-7 text-center" title="Scroll of Strength">
                Str
              </span>
            </div>
            <ul className="space-y-1">
              {view.offenders.map((o, i) => (
                <li
                  key={`${o.name}-${i}`}
                  className="flex items-center gap-2 min-w-0"
                >
                  <div className="flex-1 min-w-0">
                    <PlayerCell name={o.name} type={o.type} icon={o.icon} />
                  </div>
                  <SlotDot pct={o.battlePct} />
                  <SlotDot pct={o.guardianPct} />
                  <SlotDot pct={o.isPhysical ? o.agiPct : null} />
                  <SlotDot pct={o.isPhysical ? o.strPct : null} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}

function SlotDot({ pct }: { pct: number | null }) {
  // Null = not applicable (e.g. scroll slots for caster/healer specs).
  // Render a dim placeholder so column alignment stays consistent.
  if (pct === null) {
    return (
      <span className="w-7 flex justify-center">
        <span className="block h-2 w-2 rounded-full bg-gray-700/40" />
      </span>
    );
  }
  const color =
    pct >= FULL_COVERAGE_PCT
      ? "bg-emerald-500"
      : pct >= 50
        ? "bg-amber-400"
        : "bg-red-500";
  return (
    <span className="w-7 flex justify-center" title={`${pct.toFixed(0)}%`}>
      <span className={`block h-2 w-2 rounded-full ${color}`} />
    </span>
  );
}

function CoverageRow({
  label,
  pct,
  suffix,
}: {
  label: string;
  pct: number;
  suffix?: string;
}) {
  const color =
    pct >= 95
      ? "#34d399"
      : pct >= 80
        ? "#f0b232"
        : pct >= 50
          ? "#fb923c"
          : "#ef4444";
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="flex-1 min-w-0 truncate text-gray-300">{label}</span>
      <span className="w-12 text-right tabular-nums text-gray-200">
        {pct.toFixed(0)}%
      </span>
      <div className="w-20">
        <ProgressBar pct={pct} color={color} height={4} />
      </div>
      {suffix && (
        <span className="w-10 text-right tabular-nums text-gray-500">
          {suffix}
        </span>
      )}
    </div>
  );
}

function isPhysicalScrollSpec(
  type: string | undefined | null,
  spec: string | undefined | null,
): boolean {
  if (type === "Mage" || type === "Priest" || type === "Warlock") return false;
  if (type === "Warrior" || type === "Rogue" || type === "Hunter") return true;
  switch (spec ?? "") {
    case "Druid-Feral":
    case "Paladin-Protection":
    case "Paladin-Retribution":
    case "Shaman-Enhancement":
      return true;
    default:
      return false;
  }
}

/* ------------------------------ Avoidable ------------------------------- */

interface AvoidableRow {
  name?: string;
  type?: string;
  icon?: string;
  total: number;
}

function AvoidableCard({ report }: { report: ReportDoc }) {
  const slug = useMatch("/r/:slug/:metric")?.params.slug ?? "";
  const rows = useMemo(
    () =>
      (
        (report.metrics["avoidable_damage_taken"]
          ?.rows as unknown as AvoidableRow[]) ?? []
      ).slice(0, 5),
    [report],
  );
  const top = rows[0]?.total ?? 0;
  const raidTotal = rows.reduce((a, r) => a + r.total, 0);
  return (
    <Card
      title="Avoidable Damage"
      href={`/r/${slug}/avoidable_damage_taken`}
      tone="danger"
    >
      {!rows.length ? (
        <p className="text-xs text-gray-500">
          Nobody took avoidable damage. gg
        </p>
      ) : (
        <>
          <ol className="space-y-2">
            {rows.map((r, i) => {
              const cls = classFor(r.type);
              const pct = top ? (r.total / top) * 100 : 0;
              return (
                <li key={`${r.name}-${i}`} className="flex items-center gap-2">
                  <span className="w-4 text-xs tabular-nums text-gray-500">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <PlayerCell name={r.name} type={r.type} icon={r.icon} />
                      <span className="text-sm tabular-nums font-semibold text-red-300">
                        {fmtCompact(r.total)}
                      </span>
                    </div>
                    <ProgressBar pct={pct} color={cls.color} height={4} />
                  </div>
                </li>
              );
            })}
          </ol>
          <div className="mt-3 pt-2 border-t border-border/60 flex items-baseline justify-between text-xs text-gray-500">
            <span>Raid total</span>
            <span className="tabular-nums text-gray-300 font-semibold">
              {fmt(raidTotal)}
            </span>
          </div>
        </>
      )}
    </Card>
  );
}

/* ------------------------------ Debuffs --------------------------------- */

interface DebuffRow {
  ability?: string;
  icon?: string;
  uptimePct?: number;
  totalCasts?: number;
}

function DebuffsCard({ report }: { report: ReportDoc }) {
  const slug = useMatch("/r/:slug/:metric")?.params.slug ?? "";
  const rows = useMemo(
    () =>
      (
        (report.metrics["debuff_uptime"]?.rows as unknown as DebuffRow[]) ?? []
      ).slice(0, 8),
    [report],
  );

  if (!rows.length) {
    return (
      <Card title="Debuff Uptime" href={`/r/${slug}/debuff_uptime`}>
        <p className="text-xs text-gray-500">No tracked debuffs.</p>
      </Card>
    );
  }

  return (
    <Card title="Debuff Uptime" href={`/r/${slug}/debuff_uptime`}>
      <ul className="space-y-1.5">
        {rows.map((r, i) => {
          const pct = r.uptimePct ?? 0;
          const color =
            pct >= 95
              ? "#34d399"
              : pct >= 80
                ? "#f0b232"
                : pct >= 50
                  ? "#fb923c"
                  : "#ef4444";
          return (
            <li
              key={`${r.ability}-${i}`}
              className="flex items-center gap-2 text-xs"
            >
              <SpellIcon icon={r.icon} alt={r.ability ?? ""} size={16} />
              <span className="flex-1 min-w-0 truncate text-gray-300 font-medium">
                {r.ability}
              </span>
              <span className="w-12 text-right tabular-nums text-gray-200">
                {pct.toFixed(0)}%
              </span>
              <div className="w-24">
                <ProgressBar pct={pct} color={color} height={4} />
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/* ------------------------------ Engineering ----------------------------- */

interface EngineeringRow {
  name?: string;
  type?: string;
  icon?: string;
  total: number;
  totalCount: number;
  byItem: Record<string, { damage: number; count: number }>;
}

function EngineeringCard({ report }: { report: ReportDoc }) {
  const slug = useMatch("/r/:slug/:metric")?.params.slug ?? "";
  const block = report.metrics["engineering_damage"];
  const rows = (block?.rows as unknown as EngineeringRow[]) ?? [];
  const totals = (block?.totals ?? {}) as {
    raidTotal?: number;
    raidCount?: number;
  };

  // Aggregate raid-wide damage + uses per item, sorted by damage. Top 3
  // surface as "biggest hitters" - unlocks at a glance which sappers /
  // bombs the raid actually leans on.
  const topItems = useMemo(() => {
    const agg: Record<string, { damage: number; count: number }> = {};
    for (const r of rows) {
      for (const [item, stats] of Object.entries(r.byItem ?? {})) {
        const e = agg[item] ?? { damage: 0, count: 0 };
        e.damage += stats.damage;
        e.count += stats.count;
        agg[item] = e;
      }
    }
    return Object.entries(agg)
      .sort((a, b) => b[1].damage - a[1].damage)
      .slice(0, 3);
  }, [rows]);

  const topItemDamage = topItems[0]?.[1].damage ?? 0;
  const topContributors = rows.slice(0, 1);

  return (
    <Card title="Engineering" href={`/r/${slug}/engineering_damage`}>
      {!rows.length ? (
        <p className="text-xs text-gray-500">No engineering activity.</p>
      ) : (
        <div className="space-y-3 text-xs">
          {/* Top-line totals */}
          <div className="flex items-baseline gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">
                Damage
              </div>
              <div className="tabular-nums font-bold text-gray-100 text-sm">
                {fmtCompact(totals.raidTotal ?? 0)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">
                Uses
              </div>
              <div className="tabular-nums font-bold text-gray-100 text-sm">
                {totals.raidCount ?? 0}
              </div>
            </div>
          </div>

          {/* Top items by damage */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">
              Top items
            </div>
            <ul className="space-y-1.5">
              {topItems.map(([name, stats]) => {
                const pct = topItemDamage
                  ? (stats.damage / topItemDamage) * 100
                  : 0;
                return (
                  <li key={name} className="flex items-center gap-2">
                    <span className="flex-1 min-w-0 truncate text-gray-200">
                      {name}
                    </span>
                    <span className="tabular-nums text-gray-400 w-14 text-right">
                      {fmtCompact(stats.damage)}
                    </span>
                    <span className="tabular-nums text-gray-500 w-8 text-right">
                      ×{stats.count}
                    </span>
                    <div className="w-12">
                      <ProgressBar pct={pct} color="#f0b232" height={3} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Top contributor */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">
              Top contributor
            </div>
            <ol className="space-y-1.5">
              {topContributors.map((p, i) => (
                <li key={`${p.name}-${i}`} className="flex items-center gap-3">
                  <PlayerCell name={p.name} type={p.type} icon={p.icon} />
                  <span className="tabular-nums text-gray-300">
                    {fmtCompact(p.total)}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ------------------------------ Raid stats ------------------------------ */

function RaidStatsCard({ report }: { report: ReportDoc }) {
  const slug = useMatch("/r/:slug/:metric")?.params.slug ?? "";
  const stats = useMemo(() => {
    const fights: Fight[] = report.fights ?? [];
    const bossFights = fights.filter((f) => (f.encounterID ?? 0) !== 0);
    const kills = bossFights.filter((f) => f.kill).length;
    const wipes = bossFights.length - kills;

    // Wall-clock raid duration: first pull engagement → last successful
    // boss kill. Falls back to the very last fight end when nothing was
    // killed (full wipe night). Both startTime and endTime are
    // report-relative ms, so the diff is the duration directly.
    const startTimes = fights.map(
      (f) => f.startTime ?? Number.POSITIVE_INFINITY,
    );
    const firstStart = startTimes.length ? Math.min(...startTimes) : 0;
    const killEnds = bossFights
      .filter((f) => f.kill)
      .map((f) => f.endTime ?? 0);
    const lastEnd = killEnds.length
      ? Math.max(...killEnds)
      : Math.max(...fights.map((f) => f.endTime ?? 0), 0);
    const raidMs = Number.isFinite(firstStart) ? lastEnd - firstStart : 0;

    return {
      bossCount: bossFights.length,
      kills,
      wipes,
      raidMs,
      killPct: bossFights.length ? (kills / bossFights.length) * 100 : 0,
    };
  }, [report]);

  // Pull just the headline counts from each gear-audit metric block.
  // Both blocks are still computed by the backend even though the
  // sidebar entry is `gear_audit`, so we read them directly here.
  const enchTotals = (report.metrics?.enchants?.totals ?? {}) as {
    raidMissing?: number;
    raidSuboptimal?: number;
  };
  const gemTotals = (report.metrics?.gems?.totals ?? {}) as {
    raidEmpty?: number;
    raidUncommon?: number;
  };
  const hasEnchants = !!report.metrics?.enchants;
  const hasGems = !!report.metrics?.gems;

  // Sum deaths across the damage-taken rows. Each row carries a `deaths`
  // count per player; the metric block doesn't precompute the raid total
  // so we do it here.
  const totalDeaths = useMemo(() => {
    const rows = (report.metrics?.total_damage_taken?.rows ?? []) as {
      deaths?: number;
    }[];
    return rows.reduce((a, r) => a + (r.deaths ?? 0), 0);
  }, [report]);

  return (
    <Card title="Raid">
      <dl className="space-y-2 text-xs">
        <Stat label="Bosses">
          <span className="tabular-nums font-semibold text-gray-100">
            {stats.bossCount}
          </span>
        </Stat>
        <Stat label="Wipes">
          <span
            className={
              "tabular-nums font-semibold " +
              (stats.wipes > 0 ? "text-red-300" : "text-gray-500")
            }
          >
            {stats.wipes}
          </span>
        </Stat>
        <Stat label="Deaths">
          <span
            className={
              "tabular-nums font-semibold " +
              (totalDeaths > 0 ? "text-red-300" : "text-gray-500")
            }
          >
            {totalDeaths}
          </span>
        </Stat>
        <Stat label="Raid duration">
          <span
            className="tabular-nums text-gray-100"
            title="First pull → last boss kill"
          >
            {formatDuration(stats.raidMs)}
          </span>
        </Stat>
        {(hasEnchants || hasGems) && (
          <div className="!mt-3 pt-2 border-t border-border/60 space-y-2">
            {hasEnchants && (
              <Link
                to={`/r/${slug}/gear_audit`}
                className="block -mx-1.5 px-1.5 py-0.5 rounded hover:bg-white/5 transition-colors"
              >
                <Stat label="Enchant issues">
                  <IssueCounts
                    redCount={enchTotals.raidMissing ?? 0}
                    redLabel="missing"
                    amberCount={enchTotals.raidSuboptimal ?? 0}
                    amberLabel="suboptimal"
                  />
                </Stat>
              </Link>
            )}
            {hasGems && (
              <Link
                to={`/r/${slug}/gear_audit`}
                className="block -mx-1.5 px-1.5 py-0.5 rounded hover:bg-white/5 transition-colors"
              >
                <Stat label="Gem issues">
                  <IssueCounts
                    redCount={gemTotals.raidEmpty ?? 0}
                    redLabel="empty"
                    amberCount={gemTotals.raidUncommon ?? 0}
                    amberLabel="suboptimal"
                  />
                </Stat>
              </Link>
            )}
          </div>
        )}
      </dl>
    </Card>
  );
}

function IssueCounts({
  redCount,
  redLabel,
  amberCount,
  amberLabel,
}: {
  redCount: number;
  redLabel: string;
  amberCount: number;
  amberLabel: string;
}) {
  return (
    <span className="tabular-nums">
      <span
        className={
          "font-semibold " + (redCount > 0 ? "text-red-300" : "text-gray-500")
        }
      >
        {redCount}
      </span>
      <span className="text-gray-500"> {redLabel}</span>
      <span className="text-gray-700 mx-1.5">·</span>
      <span
        className={
          "font-semibold " +
          (amberCount > 0 ? "text-amber-300" : "text-gray-500")
        }
      >
        {amberCount}
      </span>
      <span className="text-gray-500"> {amberLabel}</span>
    </span>
  );
}

/* ------------------------------ Bits ------------------------------------ */

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 min-w-0">
      <dt className="text-gray-500 flex-shrink-0">{label}</dt>
      <dd className="min-w-0 truncate text-right">{children}</dd>
    </div>
  );
}

function formatDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}
